/**
 * React hook that returns date/time formatting functions
 * pre-bound with the user-selected language and the merchant's
 * region/timezone.
 *
 * The language for weekday/month names follows the admin language
 * switcher (LanguageContext.currentLanguage) so dates localize like
 * the rest of the UI, while the merchant's country code keeps regional
 * date conventions (e.g. 28.06.2026 vs 6/28/2026) and the merchant's
 * timezone is preserved.
 *
 * Usage:
 *   const { formatDateTime, formatDate, formatTime } = useDateFormatter();
 *   <span>{formatDateTime(booking.start_date)}</span>
 */

import { useCallback } from 'react';
import { useMerchant } from '../contexts/MerchantContext';
import { useLanguage } from '../contexts/LanguageContext';
import {
  getMerchantLocale,
  formatDateTime as _formatDateTime,
  formatDate as _formatDate,
  formatDateLong as _formatDateLong,
  formatDateShort as _formatDateShort,
  formatTime as _formatTime,
  formatDateTimeCompact as _formatDateTimeCompact,
  formatMonthYear as _formatMonthYear,
} from '../lib/dateFormatting';

export function useDateFormatter() {
  const { merchant, merchantLocale } = useMerchant();
  const { currentLanguage } = useLanguage();
  const { timezone } = merchantLocale;

  // Use the selected UI language for weekday/month names, paired with the
  // merchant's country for regional date conventions. Falls back to the
  // merchant locale's region when no country code is available.
  const locale = getMerchantLocale(currentLanguage, merchant?.country_code);

  const formatDateTime = useCallback(
    (date: Date | string) => _formatDateTime(date, locale, timezone),
    [locale, timezone]
  );

  const formatDate = useCallback(
    (date: Date | string) => _formatDate(date, locale, timezone),
    [locale, timezone]
  );

  const formatDateLong = useCallback(
    (date: Date | string) => _formatDateLong(date, locale, timezone),
    [locale, timezone]
  );

  const formatDateShort = useCallback(
    (date: Date | string) => _formatDateShort(date, locale, timezone),
    [locale, timezone]
  );

  const formatTime = useCallback(
    (date: Date | string) => _formatTime(date, locale, timezone),
    [locale, timezone]
  );

  const formatDateTimeCompact = useCallback(
    (date: Date | string) => _formatDateTimeCompact(date, locale, timezone),
    [locale, timezone]
  );

  const formatMonthYear = useCallback(
    (date: Date | string) => _formatMonthYear(date, locale, timezone),
    [locale, timezone]
  );

  return {
    formatDateTime,
    formatDate,
    formatDateLong,
    formatDateShort,
    formatTime,
    formatDateTimeCompact,
    formatMonthYear,
    locale,
    timezone,
  };
}
